import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Megaphone } from "lucide-react";
import { useAdImpression, trackAdClick } from "@/lib/adTracking";

/** Inline ad strip used between vendor search results and on app landings.
 *  Backend slot whitelist: `search-top`, `search-mid`,
 *  `app-gharonda`, `app-khavayye`, `app-soulmates`, `app-sabkuchfresh`.
 *  Returns null when the slot has no active ad (no canned fallback).
 */

// Per-tab fetch coalesce: when a single page mounts many InlineAdBanner
// instances for the same (slot, city, category), share one in-flight
// promise so we don't fan out 10+ identical /api/ads/inline calls.
// Cached responses are kept ~30s, then refetched.
const _adCache = new Map(); // key -> { promise, ts }
const _TTL_MS = 30_000;

function fetchInlineAds(slot, city, category) {
  const key = JSON.stringify({ slot, city, category });
  const entry = _adCache.get(key);
  if (entry && Date.now() - entry.ts < _TTL_MS) return entry.promise;
  const params = { slot };
  if (city) params.city = city;
  if (category) params.category = category;
  const promise = api.get("/ads/inline", { params }).then((r) => r.data || []);
  _adCache.set(key, { promise, ts: Date.now() });
  return promise;
}

export default function InlineAdBanner({ slot, city = "", category = "", className = "" }) {
  const [ad, setAd] = useState(null);

  useEffect(() => {
    let stop = false;
    fetchInlineAds(slot, city, category)
      .then((list) => {
        if (stop) return;
        if (list?.length) setAd(list[Math.floor(Math.random() * list.length)]);
        else setAd(null);
      })
      .catch(() => {});
    return () => { stop = true; };
  }, [slot, city, category]);

  useAdImpression(ad?.ad_id);

  if (!ad) return null;

  const hasImage = Boolean(ad.image_url);

  return (
    <Card className={`overflow-hidden border-border/60 ${className}`} data-testid={`inline-ad-${slot}`}>
      <a
        href={ad.url || "#"}
        onClick={() => trackAdClick(ad.ad_id)}
        className={`flex items-stretch ${hasImage ? "min-h-[96px]" : ""}`}
        target={ad.url?.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
      >
        <div
          className="w-2 shrink-0"
          style={{ background: ad.color || "#dc2626" }}
        />
        <div className={`flex-1 flex items-center gap-3 px-4 sm:px-5 ${hasImage ? "py-4 sm:py-5" : "py-3 sm:py-4"}`}>
          <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: `${ad.color || "#dc2626"}1f`, color: ad.color || "#dc2626" }}>
            <Megaphone className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-semibold truncate ${hasImage ? "text-base sm:text-lg" : "text-sm sm:text-base"}`}>{ad.title}</div>
            {ad.subtitle && (
              <div className={`text-muted-foreground truncate ${hasImage ? "text-sm" : "text-xs sm:text-sm"}`}>{ad.subtitle}</div>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground shrink-0 hidden sm:inline">
            Sponsored
          </span>
        </div>
        {hasImage && (
          <img
            src={ad.image_url}
            alt=""
            className="hidden md:block w-48 h-full object-cover self-stretch"
          />
        )}
      </a>
    </Card>
  );
}
